import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, Clock, Eye, Tag, ChevronRight, Search, Star,
  BookOpen, Menu, X, CalendarDays, MessageCircle
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'all', label: 'Všetky' },
  { id: 'bezpecnost', label: 'Bezpečnosť' },
  { id: 'technologie', label: 'Technológie' },
  { id: 'servis', label: 'Servis' },
  { id: 'tipy', label: 'Tipy' },
  { id: 'legislativa', label: 'Legislatíva' },
  { id: 'interiér', label: 'Interiér' },
];

const Logo = ({ className = "" }) => (
  <Link to="/" className={`flex items-center ${className}`}>
    <span className="text-white font-bold text-2xl tracking-tighter">Jasne</span>
    <span className="text-[#00BFFF] font-bold text-2xl tracking-tighter">Svetla</span>
  </Link>
);

const BlogHeader = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#05050A]/90 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Logo />
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-zinc-400 hover:text-white transition-colors text-sm font-medium">Domov</Link>
            <Link to="/blog" className="text-[#00BFFF] text-sm font-medium">Blog</Link>
            <Link to="/#services" className="text-zinc-400 hover:text-white transition-colors text-sm font-medium">Služby</Link>
            <Link to="/#contact" className="text-zinc-400 hover:text-white transition-colors text-sm font-medium">Kontakt</Link>
          </nav>
          <Link to="/#booking" className="hidden md:flex items-center gap-2 bg-[#00BFFF] text-[#05050A] px-6 py-2.5 font-semibold hover:bg-[#33CCFF] transition-all hover:shadow-[0_0_20px_rgba(0,191,255,0.4)]">
            <CalendarDays size={18} />
            Rezervovať
          </Link>
          <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#05050A]/95 backdrop-blur-xl border-t border-white/10">
          <nav className="flex flex-col p-6 gap-4">
            <Link to="/" className="text-zinc-400 hover:text-white transition-colors font-medium py-2">Domov</Link>
            <Link to="/blog" className="text-[#00BFFF] font-medium py-2">Blog</Link>
            <Link to="/#booking" className="text-zinc-400 hover:text-white transition-colors font-medium py-2">Rezervácia</Link>
            <Link to="/#contact" className="text-zinc-400 hover:text-white transition-colors font-medium py-2">Kontakt</Link>
          </nav>
        </div>
      )}
    </header>
  );
};

const FeaturedCard = ({ post }) => (
  <Link 
    to={`/blog/${post.slug}`} 
    data-testid={`featured-post-${post.slug}`}
    className="group relative block overflow-hidden border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition-all duration-300"
  >
    <div className="absolute top-4 left-4 z-10">
      <span className="inline-flex items-center gap-1 bg-[#00BFFF] text-[#05050A] text-xs font-bold px-3 py-1 uppercase tracking-wider">
        <Star size={12} />
        Odporúčané
      </span>
    </div>
    <div className="p-8 pt-14">
      <span className="text-[#00BFFF] text-xs font-bold uppercase tracking-[0.2em]">{post.category}</span>
      <h3 className="text-xl md:text-2xl font-medium text-white mt-3 mb-4 group-hover:text-[#00BFFF] transition-colors leading-tight">
        {post.title}
      </h3>
      <p className="text-zinc-400 text-sm leading-relaxed line-clamp-3 mb-6">{post.excerpt}</p>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 text-zinc-500 text-xs">
          <span className="flex items-center gap-1"><Eye size={14} /> {post.views}</span>
          <span className="flex items-center gap-1"><Clock size={14} /> {new Date(post.created_at).toLocaleDateString('sk-SK')}</span>
        </div>
        <span className="text-[#00BFFF] text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
          Čítať <ChevronRight size={16} />
        </span>
      </div>
    </div>
  </Link>
);

const PostCard = ({ post }) => (
  <Link 
    to={`/blog/${post.slug}`}
    data-testid={`blog-post-${post.slug}`}
    className="group block border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition-all duration-300 p-6"
  >
    <span className="text-[#00BFFF] text-xs font-bold uppercase tracking-[0.2em]">{post.category}</span>
    <h3 className="text-lg font-medium text-white mt-2 mb-3 group-hover:text-[#00BFFF] transition-colors leading-tight line-clamp-2">
      {post.title}
    </h3>
    <p className="text-zinc-400 text-sm leading-relaxed line-clamp-2 mb-4">{post.excerpt}</p>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3 text-zinc-500 text-xs">
        <span className="flex items-center gap-1"><Eye size={12} /> {post.views}</span>
        <span className="flex items-center gap-1"><Clock size={12} /> {new Date(post.created_at).toLocaleDateString('sk-SK')}</span>
      </div>
      <ChevronRight size={16} className="text-zinc-500 group-hover:text-[#00BFFF] transition-colors" />
    </div>
  </Link>
);

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const params = {};
        if (activeCategory !== 'all') params.category = activeCategory;
        const response = await axios.get(`${API}/blog`, { params });
        setPosts(response.data);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        setLoading(false);
      }
    };
    setLoading(true);
    fetchPosts();
  }, [activeCategory]);

  const filteredPosts = searchQuery
    ? posts.filter(p => 
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : posts;

  const featuredPosts = filteredPosts.filter(p => p.featured);
  const regularPosts = filteredPosts.filter(p => !p.featured);

  return (
    <div className="min-h-screen bg-[#05050A]">
      <BlogHeader />

      {/* Hero */}
      <section className="py-20 md:py-28 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-6">
            <BookOpen className="text-[#00BFFF]" size={20} />
            <span className="text-[#00BFFF] text-xs font-bold uppercase tracking-[0.2em]">Blog</span>
          </div>
          <h1 data-testid="blog-title" className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light text-white mb-6">
            Všetko o <span className="text-[#00BFFF]">svetlometoch</span>
          </h1>
          <p className="text-zinc-400 text-base md:text-lg max-w-2xl leading-relaxed">
            Odborné články, tipy a novinky zo sveta automobilového osvetlenia. Poradenstvo, ktoré vám pomôže udržať vaše svetlá v najlepšom stave.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 border-b border-white/10 sticky top-20 z-40 bg-[#05050A]/95 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  data-testid={`filter-${cat.id}`}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-4 py-2 text-sm font-medium transition-all ${
                    activeCategory === cat.id
                      ? 'bg-[#00BFFF] text-[#05050A]'
                      : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
            <div className="relative w-full md:w-72">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                data-testid="blog-search"
                type="text"
                placeholder="Hľadať články..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 text-white text-sm placeholder-zinc-500 focus:outline-none focus:border-[#00BFFF] transition-colors"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#00BFFF] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-20">
              <BookOpen size={48} className="mx-auto text-zinc-600 mb-4" />
              <p className="text-zinc-400 text-lg">Žiadne články neboli nájdené</p>
            </div>
          ) : (
            <>
              {/* Featured */}
              {featuredPosts.length > 0 && (
                <div className="mb-16">
                  <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#00BFFF] mb-8">Odporúčané články</h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    {featuredPosts.map(post => (
                      <FeaturedCard key={post.id} post={post} />
                    ))}
                  </div>
                </div>
              )}

              {/* All posts */}
              <div>
                <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-8">
                  {activeCategory === 'all' ? 'Všetky články' : CATEGORIES.find(c => c.id === activeCategory)?.label}
                  <span className="ml-2 text-zinc-600">({regularPosts.length})</span>
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {regularPosts.map(post => (
                    <PostCard key={post.id} post={post} />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Footer with backlinks */}
      <footer className="border-t border-white/10 py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Logo />
            <div className="flex items-center gap-6 text-zinc-400 text-sm">
              <span>&copy; 2026 JasneSvetla.sk</span>
              <span>&bull;</span>
              <span>Všetky práva vyhradené</span>
            </div>
            <div className="flex items-center gap-4 text-zinc-500 text-sm">
              <Link to="/" className="hover:text-[#00BFFF] transition-colors">Domov</Link>
              <span>&bull;</span>
              <Link to="/blog" className="hover:text-[#00BFFF] transition-colors">Blog</Link>
            </div>
          </div>
          {/* SEO backlinks */}
          <div className="mt-4 flex flex-wrap gap-4 justify-center text-xs text-[#05050A]">
            <a href="https://www.stavebne-dopyty.sk" target="_blank" rel="noopener noreferrer">Stavebné dopyt</a>
            <a href="https://www.stavebne-dopyty.sk" target="_blank" rel="noopener noreferrer">Stavebné služby</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogPage;
